import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Pencil, Plus, Trash2, Upload, Building2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", sort_order: 0, active: true };
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function resolveImg(url) {
  if (!url) return "";
  if (url.startsWith("/api/")) return BACKEND + url;
  return url;
}

function BrandDialog({ open, onClose, editing, onSave }) {
  const [form, setForm] = useState(editing || { ...EMPTY });
  const [logoUrl, setLogoUrl] = useState(editing?.logo_url || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setForm(editing || { ...EMPTY });
    setLogoUrl(editing?.logo_url || "");
  }, [editing]);

  const f = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));

  const handleLogoFile = async (fileList) => {
    const file = fileList?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    if (!editing) {
      toast.message("Bitte erst Hersteller speichern, dann Logo hochladen");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/admin/vehicle-brands/${editing.id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoUrl(res.data.url);
      toast.success("Logo hochgeladen");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name ist Pflichtfeld"); return; }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? "Hersteller bearbeiten" : "Hersteller anlegen"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Name *</Label>
            <Input value={form.name} onChange={(e) => f("name")(e.target.value)}
              className="mt-1" placeholder="z.B. Volkswagen" data-testid="brand-name" />
          </div>

          <div>
            <Label>Logo</Label>
            <div className="mt-1 flex items-center gap-3">
              <div className="w-16 h-16 rounded-lg border border-[#E5E5E5] bg-[#F8FAFC] flex items-center justify-center overflow-hidden shrink-0">
                {logoUrl ? (
                  <img src={resolveImg(logoUrl)} alt="" className="w-full h-full object-contain p-1.5" />
                ) : (
                  <Building2 size={22} className="text-[#A3A3A3]" />
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/svg+xml"
                className="hidden"
                onChange={(e) => handleLogoFile(e.target.files)}
                data-testid="brand-logo-input"
              />
              <Button
                type="button" size="sm" variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || !editing}
                title={editing ? "Logo hochladen" : "Erst speichern, dann Logo hochladen"}
                data-testid="brand-logo-upload-btn"
              >
                <Upload size={14} className="mr-1.5" /> {uploading ? "Lädt…" : "Logo hochladen"}
              </Button>
            </div>
          </div>

          <div>
            <Label>Reihenfolge</Label>
            <Input type="number" value={form.sort_order} onChange={(e) => f("sort_order")(parseInt(e.target.value) || 0)}
              className="mt-1" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="brand-active-chk" checked={form.active}
              onChange={(e) => f("active")(e.target.checked)} className="accent-[#E11226]" />
            <Label htmlFor="brand-active-chk">Aktiv</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#E11226] hover:bg-[#C20E1F]" data-testid="brand-save">
            {saving ? "Speichern…" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminBrands() {
  const [brands, setBrands] = useState([]);
  const [dialog, setDialog] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/vehicle-brands");
    setBrands(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setDialog({ editing: null });
  const openEdit = (item) => setDialog({ editing: item });
  const closeDialog = () => setDialog(null);

  const handleSave = async (form) => {
    if (dialog.editing) {
      await api.put(`/admin/vehicle-brands/${dialog.editing.id}`, form);
      toast.success("Hersteller aktualisiert");
    } else {
      await api.post("/admin/vehicle-brands", form);
      toast.success("Hersteller angelegt");
    }
    await load();
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`"${item.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/vehicle-brands/${item.id}`);
      toast.success("Gelöscht");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="rf-fade-in" data-testid="admin-brands">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#E11226] font-semibold">Flotte</div>
          <h1 className="font-display text-3xl font-bold text-[#0A0A0A] mt-1">Hersteller</h1>
        </div>
        <Button onClick={openNew} className="bg-[#E11226] hover:bg-[#C20E1F]" data-testid="brand-new-btn">
          <Plus className="w-4 h-4 mr-2" /> Hersteller anlegen
        </Button>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
        {brands.length === 0 ? (
          <p className="text-[#525252] text-sm py-6 text-center">Noch keine Hersteller angelegt.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F4F4] text-xs uppercase tracking-wider text-[#525252]">
                <tr>
                  <th className="text-left p-3">Logo</th>
                  <th className="text-left p-3">Name</th>
                  <th className="text-left p-3">Reihenfolge</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5E5]">
                {brands.map((b) => (
                  <tr key={b.id} className="hover:bg-[#FAFAFA]" data-testid={`admin-brand-${b.id}`}>
                    <td className="p-3">
                      <div className="w-10 h-10 rounded-md border border-[#E5E5E5] bg-[#F8FAFC] flex items-center justify-center overflow-hidden">
                        {b.logo_url ? (
                          <img src={resolveImg(b.logo_url)} alt="" className="w-full h-full object-contain p-1" />
                        ) : (
                          <Building2 size={16} className="text-[#A3A3A3]" />
                        )}
                      </div>
                    </td>
                    <td className="p-3 font-semibold text-[#0A0A0A]">{b.name}</td>
                    <td className="p-3 text-[#525252]">{b.sort_order}</td>
                    <td className="p-3">
                      <Badge className={b.active ? "bg-emerald-100 text-emerald-800 border-0" : "bg-[#F4F4F4] text-[#525252] border-0"}>
                        {b.active ? "Aktiv" : "Inaktiv"}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2 justify-end">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(b)} data-testid={`brand-edit-${b.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="text-red-500 hover:text-red-400" onClick={() => handleDelete(b)} data-testid={`brand-delete-${b.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {dialog && (
        <BrandDialog open onClose={closeDialog} editing={dialog.editing} onSave={handleSave} />
      )}
    </div>
  );
}
